// ─────────────────────────────────────────────────────────
// lib/sentry.ts — Sentry error reporting
//
// No-ops gracefully when VITE_SENTRY_DSN is not set (local
// dev, CI). No network calls, no console noise when absent.
//
// Call initSentry() once before createRoot().render().
// Call sentrySetUser() after login, sentryClearUser() on logout.
// ─────────────────────────────────────────────────────────
import * as Sentry from '@sentry/react'
import { ENV }     from './env'

export function initSentry(): void {
  if (!ENV.SENTRY_DSN) return   // dev / CI — skip silently

  Sentry.init({
    dsn:         ENV.SENTRY_DSN,
    environment: import.meta.env.MODE,                        // 'development' | 'production'
    release:     (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,         // 10% in prod, 0 in dev
    sendDefaultPii: false,                                     // never auto-send PII
  })
}

/** Report an error to Sentry. No-op when DSN is absent. */
export function sentryCapture(
  err:  Error,
  info?: { componentStack?: string | null }
): void {
  if (!ENV.SENTRY_DSN) return
  Sentry.captureException(err, {
    contexts: info?.componentStack
      ? { react: { componentStack: info.componentStack } }
      : undefined,
  })
}

/** Attach the signed-in user's identity to all subsequent events. */
export function sentrySetUser(uid: string, email: string): void {
  if (!ENV.SENTRY_DSN) return
  Sentry.setUser({ id: uid, email })
}

/** Clear user identity on logout / account deletion. */
export function sentryClearUser(): void {
  if (!ENV.SENTRY_DSN) return
  Sentry.setUser(null)
}
